import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadBalancerAPI } from '../../api/load-balancer';
import { useRecoilState } from 'recoil';
import { authState, userStorageKey } from '../../api/state';

interface TwoFAStateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TwoFARecoveryModal = ({ isOpen, onClose }: TwoFAStateModalProps) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const [twoFAState, setTwoFAState] = useState(null);
  const location = useLocation();
  const [authStateValue, setAuthState] = useRecoilState(authState);

  const get2FAStatus = async () => {
    try {
      const response = await loadBalancerAPI.apiInstance.getTwoFAState(authStateValue?.user?.id);
      if (response.data) {
        setTwoFAState(response.data.twoFAState);
      }
    } catch {
      console.error('Fail to get 2FA state');
    }
  };

  useEffect(() => {
    if (authStateValue?.user?.id) {
      get2FAStatus();
    }
  }, [authStateValue?.user?.twoFAState, authStateValue?.user?.id]);

  const handleVerifyRecoveryCode = async () => {
    try {
      const response = await loadBalancerAPI.apiInstance.verifyRecoveryCode(authStateValue?.user?.id, verificationCode);
      if (response.data.data.success === true) {
        handleDisable2FA();
        await loadBalancerAPI.apiInstance.deleteTwoFA(authStateValue?.user?.id);
        onClose();
      } else {
        setErrorMessage('Invalid recovery code. Please try again.');
      }
    } catch {
      setErrorMessage('Failed to verify recovery code.');
    }
  };

  const handleDisable2FA = async () => {
    try {
      const response = await loadBalancerAPI.apiInstance.change2FAState(authStateValue?.user?.id, false);
      if (response.status === 200) {
        const savedValue = localStorage.getItem(userStorageKey);
        if (savedValue) {
          try {
            const parsedValue = JSON.parse(savedValue);
            if (parsedValue?.refreshToken) {
              const { data } = await loadBalancerAPI.apiInstance.refreshTokenWithUserInfo(parsedValue.refreshToken);
              setAuthState(data.data);
            }
          } catch (error) {
            console.error('Error refreshing token', error);
          }
        }
        setTwoFAState(false);
        navigate('/home');
      }
    } catch {
      console.error('Failed to change 2FA state');
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleVerifyRecoveryCode();
    }
  };

  const handleCancel = () => {
    setVerificationCode('');
    setErrorMessage('');
    onClose();
    if (location.pathname.includes('two-factor-auth')) {
      navigate('/home');
    }
  };

  return (
    <Dialog open={isOpen} fullWidth maxWidth="xs">
      <DialogTitle>Use 2FA Recovery Code</DialogTitle>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <DialogContent style={{ padding: '10px' }}>
          <FormControl component="fieldset" fullWidth>
            <Box display="flex" justifyContent="center" alignItems="center" position="relative">
              <FormControlLabel
                value="recovery"
                control={<Radio defaultChecked />}
                label="2FA Recovery"
                labelPlacement="bottom"
                style={{ marginRight: '20px' }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  height: '1px',
                  width: '119px',
                  backgroundColor: '#ccc',
                  top: '33%',
                }}
              />
              <FormControlLabel
                value="disabled"
                control={<Radio />}
                label={<span style={{ color: '#aaa' }}>2FA Disabled</span>}
                labelPlacement="bottom"
                disabled
                style={{ marginLeft: '20px' }}
              />
            </Box>
          </FormControl>
          <DialogContentText style={{ marginTop: '20px', marginBottom: '10px' }}>
            Please enter the recovery code you received when you setup your Two-Factor Authentication.
          </DialogContentText>
          <DialogContentText>Recovery code</DialogContentText>
          <TextField
            required
            margin="dense"
            label="Recovery code"
            type="text"
            fullWidth
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            error={!!errorMessage}
            helperText={errorMessage}
            onKeyDown={handleKeyDown}
          />
        </DialogContent>
      </div>
      <DialogActions style={{ flexDirection: 'column' }}>
        <Button variant="contained" onClick={handleVerifyRecoveryCode} color="primary">
          Disable 2FA
        </Button>
        <Button onClick={handleCancel} color="primary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TwoFARecoveryModal;
